import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MapBaseLayer } from 'features/mapping/mapping.types';
import { MapBaseLayerNames } from 'features/mapping/mapping.constants';
import { BasemapStyleSelection } from './BasemapStyleSelection';
import type { BasemapPreviewLayout } from './layersDrawer.shared';
import { useMapBaseLayerSelectorStyles } from './layersDrawer.styles';

type MapBaseLayerSelectorProps = {
  value: MapBaseLayer;
  onChange: (layer: MapBaseLayer) => void;
};

const IMG_URLS = {
  [MapBaseLayerNames.STREET]: '/images/street.png',
  [MapBaseLayerNames.SATELLITE]: '/images/satellite.png',
  [MapBaseLayerNames.TOPOGRAPHIC]: '/images/terrain.png',
};

const TRANSLATIONS = {
  [MapBaseLayerNames.STREET]: 'map.layers.basemaps.street',
  [MapBaseLayerNames.SATELLITE]: 'map.layers.basemaps.satellite',
  [MapBaseLayerNames.TOPOGRAPHIC]: 'map.layers.basemaps.topographic',
} as const;

const BASE_LAYER_OPTIONS = [
  MapBaseLayerNames.STREET,
  MapBaseLayerNames.SATELLITE,
  MapBaseLayerNames.TOPOGRAPHIC,
] as const;

export const MapBaseLayerSelector = (
  props: MapBaseLayerSelectorProps,
): JSX.Element => {
  const { value, onChange } = props;
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('phone'));
  const previewLayout: BasemapPreviewLayout = isPhone ? 'fluid' : 'square';
  const { classes, cx } = useMapBaseLayerSelectorStyles({
    previewLayout,
  });
  const { t } = useTranslation();

  return (
    <RadioGroup
      aria-labelledby="demo-radio-buttons-group-label"
      value={value}
      name="radio-buttons-group"
      className={classes.radioGroup}
    >
      {BASE_LAYER_OPTIONS.map((name) => {
        const isSelected = value === name;

        return (
          <FormControlLabel
            key={`base-${name}`}
            value={name}
            className={classes.option}
            control={
              <Radio
                disableRipple
                disableFocusRipple
                className={classes.hiddenRadio}
              />
            }
            label={
              <span className={classes.optionLabel}>
                <span className={classes.optionPreview}>
                  <BasemapStyleSelection
                    url={IMG_URLS[name]}
                    checked={isSelected}
                    previewLayout={previewLayout}
                  />
                </span>
                <Typography
                  variant="subtitle1"
                  className={cx(
                    classes.optionLabelText,
                    isSelected && classes.fontBold,
                  )}
                >
                  {t(TRANSLATIONS[name])}
                </Typography>
              </span>
            }
            onClick={() => onChange(name)}
          />
        );
      })}
    </RadioGroup>
  );
};
